/** Display formatting. British conventions throughout, matching the prototype. */

const DATE = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

const DATE_TIME = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
});

const TIME = new Intl.DateTimeFormat("en-GB", {
  hour: "2-digit",
  minute: "2-digit",
});

export function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  return DATE.format(new Date(value));
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  return DATE_TIME.format(new Date(value));
}

export function formatTime(value: string | null | undefined): string {
  if (!value) return "—";
  return TIME.format(new Date(value));
}

/**
 * Relative time, rounded to something a person would say.
 *
 * "7 min ago" rather than "7 minutes and 12 seconds ago": the prototype's
 * connector-health panel reads this way, and a precise figure invites the
 * reader to treat it as more meaningful than it is.
 */
export function timeAgo(value: string | null | undefined): string {
  if (!value) return "never";
  const then = new Date(value).getTime();
  const seconds = Math.round((Date.now() - then) / 1000);

  if (seconds < 0) {
    const ahead = Math.abs(seconds);
    if (ahead < 90) return "in a moment";
    if (ahead < 3600) return `in ${Math.round(ahead / 60)} min`;
    if (ahead < 86400) return `in ${Math.round(ahead / 3600)} h`;
    return `in ${Math.round(ahead / 86400)} days`;
  }
  if (seconds < 45) return "just now";
  if (seconds < 3600) return `${Math.round(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)} h ago`;
  if (seconds < 172800) return "yesterday";
  if (seconds < 604800) return `${Math.round(seconds / 86400)} days ago`;
  return formatDate(value);
}

export function formatBytes(bytes: number): string {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const index = Math.min(
    units.length - 1,
    Math.floor(Math.log(bytes) / Math.log(1024)),
  );
  const size = bytes / 1024 ** index;
  return `${size >= 10 || index === 0 ? Math.round(size) : size.toFixed(1)} ${units[index]}`;
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat("en-GB").format(value);
}

export function formatCost(value: number): string {
  // Four decimal places: a day of development traffic costs cents, and
  // rounding to two would show every figure as £0.00.
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: value < 1 ? 4 : 2,
    maximumFractionDigits: 4,
  }).format(value);
}

export function pageRange(from: number, to: number): string {
  return from === to ? `page ${from}` : `pages ${from}–${to}`;
}

/** Renders the server's <mark> highlight without trusting arbitrary HTML. */
export function splitHighlight(highlight: string): { text: string; mark: boolean }[] {
  if (!highlight) return [];
  return highlight
    .split(/(<mark>.*?<\/mark>)/g)
    .filter(Boolean)
    .map((part) =>
      part.startsWith("<mark>")
        ? { text: part.slice(6, -7), mark: true }
        : { text: part, mark: false },
    );
}
